import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Video, Play, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Videos = () => {
  const { theme } = useTheme();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const res = await axios.get(`${API}/videos?limit=50`);
      setVideos(res.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEmbedUrl = (video) => {
    const url = video.video_url;
    
    if (video.embed_type === 'youtube' || url.includes('youtube.com') || url.includes('youtu.be')) {
      const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (match) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }
    
    if (video.embed_type === 'vimeo' || url.includes('vimeo.com')) {
      const match = url.match(/vimeo\.com\/(\d+)/);
      if (match) {
        return `https://player.vimeo.com/video/${match[1]}`;
      }
    }
    
    return null;
  };

  const getThumbnail = (video) => {
    if (video.thumbnail) return video.thumbnail;
    
    const url = video.video_url;
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (match) {
        return `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg`;
      }
    }
    
    return null;
  };

  const GlassCard = ({ children, className = '' }) => (
    <div className={`bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl ${className}`}>
      {children}
    </div>
  );

  return (
    <div className="py-4">
      <div className="max-w-6xl mx-auto px-3 sm:px-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 
            className="text-2xl sm:text-3xl font-black uppercase tracking-wider mb-1 flex items-center gap-2"
            style={{ color: theme.primary, textShadow: theme.glow }}
            data-testid="videos-title"
          >
            <Video className="w-6 h-6 sm:w-8 sm:h-8" />
            Videos
          </h1>
          <p className="text-sm text-gray-400">Watch performances, behind the scenes & more</p>
        </div>

        {/* Featured Video */}
        {selectedVideo && (
          <GlassCard className="mb-8 overflow-hidden">
            <div className="aspect-video">
              <iframe
                src={getEmbedUrl(selectedVideo)}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="p-4">
              <h2 className="font-bold text-xl" style={{ color: theme.primary }}>{selectedVideo.title}</h2>
              {selectedVideo.description && (
                <p className="text-gray-400 mt-2">{selectedVideo.description}</p>
              )}
            </div>
          </GlassCard>
        )}

        {/* Video Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map((video, index) => {
            const thumbnail = getThumbnail(video);
            const isSelected = selectedVideo?.id === video.id;
            
            return (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => setSelectedVideo(video)}
                className={`cursor-pointer group ${isSelected ? 'ring-2' : ''}`}
                style={{ '--ring-color': theme.primary, ringColor: isSelected ? theme.primary : 'transparent' }}
                data-testid={`video-${video.id}`}
              >
                <GlassCard className="overflow-hidden">
                  <div className="aspect-video relative bg-black/50">
                    {thumbnail ? (
                      <img 
                        src={thumbnail} 
                        alt={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-12 h-12 text-gray-600" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: theme.primary }}
                      >
                        <Play className="w-8 h-8 text-black ml-1" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold truncate">{video.title}</h3>
                    {video.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{video.description}</p>
                    )}
                  </div>
                </GlassCard>
              </motion.div>
            );
          })}
        </div>

        {videos.length === 0 && !loading && (
          <GlassCard className="p-16 text-center">
            <Video className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-500 text-lg">No videos yet</p>
            <p className="text-gray-600 text-sm mt-2">Check back soon for updates!</p>
          </GlassCard>
        )}
      </div>
    </div>
  );
};

export default Videos;
